import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import type { SpaceAction } from '../types';

export type WireHandler = (action: SpaceAction, wireId: string) => void;

export interface SpaceBus {
  /** Unique per mounted Space instance — every delivery is scoped to it so concurrently-open Spaces never cross (SC6). */
  instanceId: string;
  publish: (sectionId: string, action: SpaceAction, wireId: string) => void;
  /** Returns an unsubscribe function. */
  subscribe: (sectionId: string, handler: WireHandler) => () => void;
}

const SpaceBusContext = createContext<SpaceBus | null>(null);

export const useSpaceBus = (): SpaceBus => {
  const ctx = useContext(SpaceBusContext);
  if (!ctx) {
    throw new Error('useSpaceBus must be used within a SpaceBusProvider');
  }
  return ctx;
};

export const useSpaceBusOrNull = (): SpaceBus | null => useContext(SpaceBusContext);

export interface SpaceBusProviderProps {
  /** Unique per mounted Space instance (e.g. one per `SpaceRendererOrganism` mount). */
  instanceId: string;
  children: React.ReactNode;
}

/**
 * Namespaces wire delivery to one Space instance over a plain in-memory pub/sub — no dependency on
 * the PWA's Redux store, so `CompositionHost`/`SpaceRendererOrganism` stay host-agnostic. The legacy
 * global bus bridge is injected by the host via a callback, not read from here.
 */
export const SpaceBusProvider: React.FC<SpaceBusProviderProps> = ({ instanceId, children }) => {
  const handlersRef = useRef<Map<string, Set<WireHandler>>>(new Map());

  const publish = useCallback((sectionId: string, action: SpaceAction, wireId: string) => {
    // Snapshot before dispatch: a handler is free to (un)subscribe while it runs — an adapter
    // re-registering during its own delivery would otherwise be invoked twice by the same publish,
    // and one unsubscribing mid-dispatch would skip a sibling still in the live Set.
    const handlers = handlersRef.current.get(sectionId);
    if (!handlers) return;
    [...handlers].forEach((handler) => handler(action, wireId));
  }, []);

  const subscribe = useCallback((sectionId: string, handler: WireHandler) => {
    if (!handlersRef.current.has(sectionId)) {
      handlersRef.current.set(sectionId, new Set());
    }
    handlersRef.current.get(sectionId)!.add(handler);
    return () => {
      handlersRef.current.get(sectionId)?.delete(handler);
    };
  }, []);

  const value = useMemo<SpaceBus>(() => ({ instanceId, publish, subscribe }), [instanceId, publish, subscribe]);

  return <SpaceBusContext.Provider value={value}>{children}</SpaceBusContext.Provider>;
};
